import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Trophy,
  ChevronRight,
  Calendar,
  ArrowRight,
  Flag,
  Skull,
  HeartHandshake,
  Landmark,
  ShieldCheck,
  Crown,
  Zap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Home() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: pageContent } = useQuery({
    queryKey: ['page-content-home'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_AUTH_SERVER_URL || 'http://localhost:3001'}/api/admin/content/home`);
      const data = await response.json();
      return data.content || {};
    },
  });

  const { data: newsPosts = [] } = useQuery({
    queryKey: ['news-latest'],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.VITE_AUTH_SERVER_URL || 'http://localhost:3001'}/api/admin/news`);
      const data = await res.json();
      return Array.isArray(data) ? data.slice(0, 3) : [];
    },
  });

  // Default fallback data if API hasn't returned yet
  const content = pageContent || {
    hero: { title: "Knight Arena", subtitle: "Play your part and rise to become a legend in a dark world of kings, knights, politics and war.", ctaPrimary: "Join the Alpha", ctaSecondary: "Explore Features" },
    stats: [],
    paths: { title: "Choose Your Path", description: "Pick your faction and class to forge your legend in the realm.", items: [] },
    factions: { title: "Factions", description: "Choose your allegiance and shape the political landscape of the realm.", items: [] },
    classes: { title: "Classes", description: "Each class offers unique playstyles, multiple specializations, and endless depth for mastery.", items: [] },
    philosophy: {
      title: "Built Different",
      subtitle: "We're a small team of passionate developers creating the MMORPG we've always wanted, shaped by 25 years of playing them.",
      items: [
        { title: "No Shareholders", description: "100% independent. No investors telling us to monetize harder. We answer only to our vision and the community." },
        { title: "Never Pay to Win", description: "Skill and dedication are the only currencies that matter. We're building the game we want to play, not a cash machine." },
        { title: "Player Driven", description: "A living, persistent world. Kings rule, councils scheme, and clans of Knights clash, all roles shaping the fate of the realm by actual players." }
      ]
    },
    cta: { title: "Ready to Join the War?", subtitle: "Not a fluffy theme-park MMO.\nStart as a trainee, as the commoner you are, and fight your way up to earn your place in legend.", buttonText: "Join Now - It's Free" }
  };

  return (
    <div className="min-h-screen relative">
      {/* Unified Background - Fixed position spanning all sections */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/2a072343d_loading_screen_08.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        {/* Vignette effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      {/* All content is relative to sit above the background */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/*
            Background trailer. Sits behind the hero content (which is z-10).
            - muted + playsinline: browsers block autoplay with sound
            - loop needs `playlist` set to the same id, or it plays once
            - controls/keyboard off and pointer-events-none so it reads as
              background, not a player
            - sized to max(100vw, 177.78vh) x max(100vh, 56.25vw) so the 16:9
              frame always covers without letterboxing
            - the layer is pinned to h-screen rather than inset-0: on narrow
              screens the hero content stacks and the section grows TALLER than
              the viewport, and a viewport-sized iframe would leave a gap at the
              bottom. Anything below the fold falls through to the page
              background, which the bottom gradient already fades into.
            - motion-reduce:hidden falls back to the still background image
              already rendered behind this layer
          */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-screen z-0 overflow-hidden pointer-events-none motion-reduce:hidden"
          >
            <iframe
              title="Knight Arena trailer"
              src="https://www.youtube-nocookie.com/embed/ntcE83lqgnY?autoplay=1&mute=1&loop=1&playlist=ntcE83lqgnY&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&fs=0&iv_load_policy=3"
              allow="autoplay; encrypted-media"
              referrerPolicy="strict-origin-when-cross-origin"
              frameBorder="0"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[max(100vw,177.78vh)] h-[max(100vh,56.25vw)] max-w-none"
            />
            {/* Darken for legibility, and fade into the section below */}
            <div className="absolute inset-0 bg-[#0a0a0a]/60" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-transparent to-[#0a0a0a]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#0a0a0a_100%)]" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6">
              <span className="text-white">
                {content.hero?.title}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
              {content.hero?.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to={createPageUrl("Register")}>
                <Button size="lg" className="bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white px-8 py-6 text-lg border-none">
                  {content.hero?.ctaPrimary}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to={createPageUrl("Features")}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 hover:text-white hover:border-white px-8 py-6 text-lg">
                  {content.hero?.ctaSecondary}
                </Button>
              </Link>
            </div>

          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2" />
            </div>
          </div>
        </section>

        {/* Choose Your Path - Factions & Classes */}
        <section className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                {content.paths?.title}
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {content.paths?.description}
              </p>
            </div>

            {/* Nobility vs Criminality Path Selection */}
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-24">
              {content.paths?.items?.map((path, idx) => (
                <div key={path.id} className="relative overflow-hidden rounded-lg cursor-pointer group h-[520px] border border-[#8B0000]/30 hover:border-white/50 transition-all duration-500">
                  <img
                    src={path.image}
                    alt={path.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black from-30% via-black/70 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-8">
                    <div className="text-xs text-gray-400 uppercase tracking-[0.3em] mb-2">{path.pathLabel}</div>
                    <h4 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">{path.name}</h4>
                    <p className="text-sm text-white drop-shadow-md leading-relaxed mb-4">
                      {path.description}
                    </p>
                    <div className="flex gap-2">
                      {path.tags?.map((tag, i) => (
                        <span key={i} className="text-xs border border-white/30 bg-black/40 text-gray-300 rounded px-3 py-1">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Factions */}
            <div className="mb-20">
              <h3 className="text-3xl font-bold text-center mb-4 text-white">{content.factions?.title}</h3>
              <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
                {content.factions?.description}
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {content.factions?.items?.map((faction, idx) => {
                  const Icon = faction.name === 'House Kalivant' ? Flag : faction.name === 'House Drakonis' ? Crown : Skull;
                  return (
                    <Card 
                      key={idx} 
                      className={`bg-gradient-to-b ${faction.color} backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 hover:scale-105 cursor-pointer group`}
                    >
                      <Icon className="w-16 h-16 text-white mb-4 group-hover:scale-110 transition-transform mx-auto" />
                      <h4 className="text-2xl font-bold mb-3 text-white text-center">{faction.name}</h4>
                      <p className="text-gray-400 text-center">{faction.desc}</p>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Classes */}
            <div>
              <h3 className="text-3xl font-bold text-center mb-4 text-white">{content.classes?.title}</h3>
              <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
                {content.classes?.description}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                {content.classes?.items?.map((classInfo, idx) => (
                  <Card 
                    key={idx}
                    className={`bg-gradient-to-b ${classInfo.color} backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300`}
                  >
                    <div className="text-6xl mb-4">{classInfo.icon}</div>
                    <h4 className="text-3xl font-bold mb-4 text-white">{classInfo.name}</h4>
                    <p className="text-gray-400 mb-6">{classInfo.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="text-sm font-semibold text-gray-500 uppercase mb-3">Play Styles</h5>
                        <ul className="space-y-2">
                          {classInfo.playStyles?.map((style, i) => (
                            <li key={i} className="flex items-start text-gray-300 text-sm">
                              <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 mt-1.5 flex-shrink-0" />
                              {style}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold text-gray-500 uppercase mb-3">Key Strengths</h5>
                        <ul className="space-y-2">
                          {classInfo.strengths?.map((strength, i) => (
                            <li key={i} className="flex items-start text-gray-300 text-sm">
                              <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 mt-1.5 flex-shrink-0" />
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="text-center mt-12">
              <Link to={createPageUrl("Features")}>
                <Button variant="outline" className="border-white text-white hover:bg-white/10 hover:text-white hover:border-white">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                {content.philosophy?.title}
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                {content.philosophy?.subtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {content.philosophy?.items?.map((item, idx) => {
                const icons = [Landmark, ShieldCheck, HeartHandshake];
                const Icon = icons[idx] || Landmark;
                return (
                  <Card key={idx} className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 hover:scale-105">
                    <Icon className="w-12 h-12 text-white mb-4" />
                    <h3 className="text-2xl font-bold mb-4 text-white">{item.title}</h3>
                    <p className="text-gray-400">{item.description}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        {newsPosts.length > 0 && (
          <section className="py-24 relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  Latest Updates
                </h2>
                <Link to={createPageUrl("Community")}>
                  <Button className="bg-transparent border border-white/10 text-white hover:text-white hover:bg-white/5 hover:border-white/20">
                    View All
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {newsPosts.map((post) => (
                  <Card
                    key={post.id}
                    className="bg-[#111111] border-[#8B0000]/20 overflow-hidden hover:border-[#8B0000]/60 transition-all duration-300 cursor-pointer group"
                  >
                    {post.image_url && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      {post.is_featured === 1 && (
                        <Badge className="mb-3 bg-transparent text-white border border-[#8B0000]/60 text-xs lowercase">
                          featured
                        </Badge>
                      )}
                      <h3 className="text-lg font-bold mb-2 text-white leading-snug group-hover:text-white transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-500 text-sm mb-4 line-clamp-3">
                        {post.content}
                      </p>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Date(post.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-24 relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">
                {content.cta?.title}
              </span>
            </h2>
            <p className="text-xl text-gray-400 mb-8 whitespace-pre-line">
              {content.cta?.subtitle}
            </p>
            <Link to={createPageUrl("Register")}>
              <Button size="lg" className="bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white px-12 py-6 text-lg border-none">
                {content.cta?.buttonText}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}